// Solicitudes de transporte del pasajero (shuttle o cab). La rideRequest ES la
// reserva del pasajero en el modo puerta a puerta: la reserva la inicia
// siempre el pasajero, en todos los modos (en bus, ver buses.ts);
// el armado after la asigna a un vehiculo.
// Regla de negocio (AT-12, Federico 2026-09-24): la antelacion es por tipo de
// servicio (combi 12 h, auto 2 h por defecto, configurables por planta) y se
// cuenta desde la hora en que pasan a buscar al pasajero: al ingreso,
// shiftInstant menos el tope de trayecto; al egreso, shiftInstant. Fuera de
// esa ventana corresponde una excepcion de movilidad (ver exceptions.ts).
//
// Punto de ascenso o descenso: la rideRequest es POR COORDENADAS (puerta a
// puerta) o POR PARADA (bus), nunca ambas. Se modela como union
// discriminada por `tipo` (un refine XOR no se puede generar a Kotlin).
// Compatibilidad 0.1.x: un request sin `tipo` se acepta y el tipo se deriva
// de cual de los dos fields viene (preprocess); el wire documentado exige
// `tipo`. La forma sin `tipo` se retira en 0.2.0.
import { z } from "zod";
import { Coordinates, Integer, CalendarDate, Instant, RecordId, StopId, SiteId, RideRequestId, TripId, VehicleType, } from "./common.js";
export const RideRequestDirection = z.enum(["inbound", "outbound"]).openapi("RideRequestDirection", {
    description: "Sentido del traslado respecto de la planta: ingreso (hacia la planta) o egreso (desde la planta).",
});
// PB-20 (docs/PLAN-DESPACHO.md): sentido de una solicitud tal como queda
// guardada. Suma point_to_point (traslado libre en auto, sin la planta) a los
// dos de RideRequestDirection, que sigue cerrado donde el sentido es respecto
// de la porteria (armado, importacion, torre, proveedor): un traslado libre
// no entra ahi. Solo se lee; el alta de la app lo deriva de origen y destino.
export const RideRequestTravelDirection = z.enum(["inbound", "outbound", "point_to_point"]).openapi("RideRequestTravelDirection", {
    description: "Sentido de la solicitud: inbound (hacia la planta), outbound (desde la planta) o point_to_point (traslado libre en auto: ninguno de los dos extremos es la planta). Un cliente anterior a 0.6.22 nunca crea point_to_point.",
});
export const RideRequestChannel = z
    .enum(["app", "web", "phone", "import"])
    .openapi("RideRequestChannel", {
    description: "Canal por el que se origino la solicitud. Sirve para medir adopcion de la app frente a los canales informales de hoy. 'import' marca las solicitudes reales cargadas por lote durante el mes en sombra (R1): pedidos que ya existen en el mundo real y entran por la API de import.",
});
export const OperatingDate = CalendarDate.openapi("OperatingDate", {
    description: "Fecha operativa (dia del turno) en formato AAAA-MM-DD, en la hora local de la planta. NO es una fecha UTC: el turno de las 06:00 hora local pertenece a ese dia local aunque su instante UTC caiga en otro dia.",
    example: "2026-09-15",
});
export const RideRequestStatus = z
    .enum(["pending", "assigned", "cancelled"])
    .openapi("RideRequestStatus", {
    description: "Estado de la solicitud: pending (aun sin viaje), assigned (incluida en el armado de un viaje) o cancelled.",
});
// PB-14 (docs/PLAN-DESPACHO.md, V1): catalogo cerrado de por que el despacho
// no convirtio una solicitud en viaje. Se persiste en
// ride_requests.dispatch_issue y sale en la lectura de solicitudes de la
// planta (PB-17). Los motivos de hoy del armado se mapean uno a uno:
// AllocationRejectionReason no_default_provider -> no_provider,
// default_provider_without_fleet -> provider_without_fleet,
// preferred_provider_without_fleet -> preferred_provider_without_fleet;
// UnassignedReason direct_journey_over_max -> over_max_journey,
// duplicate_record y no_origin sin cambio.
export const DispatchIssue = z
    .enum([
    "no_provider",
    "provider_without_fleet",
    "preferred_provider_without_fleet",
    "outside_zones",
    "over_max_journey",
    "duplicate_record",
    "no_origin",
    "needs_street_routing",
    "after_close",
    "no_eligible_provider",
])
    .openapi("DispatchIssue", {
    description: "Por que el despacho no convirtio la solicitud en viaje; la solicitud sigue pending. no_provider: ni la persona ni la planta nombran un proveedor. provider_without_fleet: el proveedor por defecto no tiene unidades del servicio. preferred_provider_without_fleet: el proveedor de la politica de la persona no tiene unidades. outside_zones: el domicilio no cae en ninguna zona y la planta no tiene default. over_max_journey: el trayecto directo ya supera el tope. duplicate_record: el legajo aparece dos veces en el grupo. no_origin: sin punto de busqueda. needs_street_routing: un lugar que el ruteo por calles todavia no cubre, o el ruteo no respondio. after_close: llego despues del cierre de su grupo. no_eligible_provider (0.6.33, AT-8): ningun proveedor habilitado en la planta tiene una unidad en servicio de ese tipo; es informativo (caso borde), la solicitud no se bloquea ni se reintenta distinto.",
});
export const RideRequestPointType = z.enum(["coordinates", "stop"]).openapi("RideRequestPointType", {
    description: "Como se ubica al pasajero: coordinates (puerta a puerta, trae origin) o stop (parada fija de colectivo, trae stopId).",
});
const pointByCoordinates = {
    type: z.enum(["coordinates"]),
    origin: Coordinates.openapi({
        description: "Coordenadas de origin (o destino, si el direction es egreso): el pasajero no sube en una parada fija.",
    }),
};
const pointByStop = {
    type: z.enum(["stop"]),
    stopId: StopId.openapi({ description: "Parada fija de ascenso o descenso." }),
};
// Compatibilidad 0.1.x: deriva `tipo` cuando el cliente no lo manda y viene
// exactamente uno de origin o stopId. Con ambos o ninguno no deriva nada y
// la union responde con su error de discriminador.
function derivePointType(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        return value;
    const obj = value;
    if (obj["type"] !== undefined)
        return value;
    const hasOrigin = obj["origin"] !== undefined;
    const hasStop = obj["stopId"] !== undefined;
    if (hasOrigin === hasStop)
        return value;
    return { ...obj, type: hasOrigin ? "coordinates" : "stop" };
}
const DISCRIMINATOR_ERROR = {
    error: "tipo debe ser 'coordinates' (con origin) o 'stop' (con stopId): exactamente uno de origin o stopId",
};
const createRideRequestFields = {
    siteId: SiteId,
    recordId: RecordId,
    direction: RideRequestDirection,
    shiftInstant: Instant.openapi({
        description: "Instant del turno al que responde la solicitud (inicio para ingreso, fin para egreso), en ISO 8601 UTC. La antelacion se cuenta desde la busqueda del pasajero: al ingreso, este instante menos el tope de trayecto de la planta; al egreso, este instante. Es por tipo de servicio (combi y auto) y la configura cada planta.",
    }),
    channel: RideRequestChannel,
};
export const CreateRideRequestByCoordinates = z
    .object({ ...createRideRequestFields, ...pointByCoordinates })
    .openapi("CreateRideRequestByCoordinates", {
    description: "Solicitud puerta a puerta: el pasajero se ubica por coordenadas.",
});
export const CreateRideRequestByStop = z
    .object({ ...createRideRequestFields, ...pointByStop })
    .openapi("CreateRideRequestByStop", {
    description: "Solicitud sobre una parada fija (colectivo).",
});
export const CreateRideRequestRequest = z
    .preprocess(derivePointType, z.discriminatedUnion("type", [CreateRideRequestByCoordinates, CreateRideRequestByStop], DISCRIMINATOR_ERROR))
    .openapi("CreateRideRequestRequest", {
    description: "Pedido de creacion de una solicitud de transporte, discriminado por tipo (coordinates o stop). Multi-tenant: siempre en el contexto de una planta (siteId).",
});
// PB-17: como pidio la hora el pasajero y de donde salio el extremo que no
// es la planta. Enums propios de la lectura de planta (mobile-trips importa
// este archivo: reusar los de mobile armaria un ciclo).
export const RideRequestTimeMode = z.enum(["depart_at", "arrive_by"]).openapi("RideRequestTimeMode", {
    description: "Como pidio la hora el pasajero: depart_at ('salir a las': la busqueda es esa hora) o arrive_by ('llegar a las': la busqueda es esa hora menos el tope de trayecto de la planta).",
});
export const RideRequestPlaceKind = z.enum(["home", "point", "google_place"]).openapi("RideRequestPlaceKind", {
    description: "De donde salio el extremo que no es la planta: home (domicilio del legajo), point (punto libre del dispositivo) o google_place (lugar de la busqueda de Google, sin ruteo por calles hasta G9c).",
});
const rideRequestDispatchFields = {
    serviceType: VehicleType.optional().openapi({
        description: "Servicio con que se despacha: el pedido por la app o, en importadas sin servicio, el que resulta de la politica del legajo. Ausente en paradas de colectivo importadas.",
    }),
    timeMode: RideRequestTimeMode.optional().openapi({
        description: "Modo de la hora pedida. Ausente en importadas (traen el instante del turno).",
    }),
    requestedTime: Instant.optional().openapi({
        description: "Hora tal como la pidio el pasajero (UTC). Ausente en importadas.",
    }),
    placeKind: RideRequestPlaceKind.optional().openapi({
        description: "Origen del extremo que no es la planta. Ausente en importadas.",
    }),
    shiftId: z.string().min(1).optional().openapi({
        description: "Turno de la planta en que encajo la combi. Ausente en autos e importadas sin ancla.",
    }),
    offShift: z.boolean().optional().openapi({
        description: "true si la combi quedo lejos de su turno (fuera de la ventana de encaje): se despacha igual, la torre lo marca.",
    }),
    dispatchCloseAt: Instant.optional().openapi({
        description: "Cierre de su grupo (combi) o ventana (auto): desde ahi la toma el despacho automatico. Ausente: sin cierre, solo la arma la torre.",
    }),
    dispatchIssue: DispatchIssue.optional().openapi({
        description: "Por que el despacho no la convirtio en viaje. Solo en pendientes ya evaluadas; se limpia al asignarse.",
    }),
    dispatchCheckedAt: Instant.optional().openapi({
        description: "Ultima vez que el despacho la evaluo (UTC).",
    }),
    // 0.6.28 (seguimiento de AT-21): con el alta y el cierre la torre distingue
    // "llego despues del cierre" de "paso el cierre y no se armo sola".
    createdAt: Instant.optional().openapi({
        description: "Instante de alta de la solicitud en el sistema (UTC): el pedido de la app, la carga de la torre o la fila del lote importado. Posterior a dispatchCloseAt: llego con el cierre ya vencido. Ausente en una API anterior a 0.6.28.",
    }),
};
export const RideRequestResponse = z
    .object({
    requestId: RideRequestId,
    ...createRideRequestFields,
    direction: RideRequestTravelDirection,
    origin: Coordinates.optional().openapi({
        description: "Coordenadas de origin (o destino en egreso). Presente cuando la solicitud es puerta a puerta; entonces stopId esta ausente.",
    }),
    stopId: StopId.optional().openapi({
        description: "Parada fija de ascenso o descenso. Presente cuando la solicitud es sobre una parada; entonces origin esta ausente.",
    }),
    // Fecha operativa del turno (dia local de la site). Opcional por
    // compatibilidad con consumidores previos a R1; la API siempre la entrega.
    date: OperatingDate.optional(),
    status: RideRequestStatus,
    // Presente solo cuando el armado ya asigno la rideRequest a un trip.
    tripId: TripId.optional(),
    // PB-17 (docs/PLAN-DESPACHO.md): datos del despacho para la bandeja de
    // la torre. Todos opcionales: un consumidor anterior los ignora. Nunca
    // la etiqueta del lugar (es del pasajero).
    ...rideRequestDispatchFields,
})
    .openapi("RideRequestResponse", {
    description: "Solicitud de transporte tal como la conoce el sistema, con su identificador y estado. Exactamente uno de origin o stopId esta presente (lo garantiza el servidor).",
});
// --- Importacion por lote (R1 "sombra operable") -----------------------------
// Las ~400 rideRequests reales del dia llegan como lote JSON. El importador
// provee el id de cada fila: re-importar el mismo archivo cae en filas
// "duplicada" y no genera datos nuevos (idempotencia del lote por PK).
/** Tope de filas por lote: cubre el dia completo de la operacion relevada. */
export const MAX_REQUESTS_PER_BATCH = 500;
// Fila del lote: como CreateRideRequestRequest pero con id provisto por el
// importador, sin siteId (va en el path) y sin channel (siempre import).
// La fecha operativa es opcional: si falta, el servidor la deriva de
// shiftInstant en la zona horaria de la site.
// NOTA: la antelacion NO se valida al importar: son pedidos
// historicos o del dia que ya existen en el mundo real; la regla aplica a
// rideRequests nuevas creadas por el pasajero (app/web/phone).
const importedRideRequestFields = {
    requestId: RideRequestId.openapi({
        description: "Id provisto por el importador, estable entre corridas: es la clave de idempotencia del lote (re-importar el mismo archivo no duplica).",
    }),
    recordId: RecordId,
    direction: RideRequestDirection,
    date: OperatingDate.optional().openapi({
        description: "Fecha operativa del turno. Si falta, el servidor la deriva de shiftInstant en la zona horaria de la planta (nunca en UTC).",
    }),
    shiftInstant: createRideRequestFields.shiftInstant,
};
export const ImportedRideRequestByCoordinates = z
    .object({ ...importedRideRequestFields, ...pointByCoordinates })
    .openapi("ImportedRideRequestByCoordinates", {
    description: "Fila del lote ubicada por coordenadas (puerta a puerta).",
});
export const ImportedRideRequestByStop = z
    .object({ ...importedRideRequestFields, ...pointByStop })
    .openapi("ImportedRideRequestByStop", {
    description: "Fila del lote ubicada por parada fija (colectivo).",
});
export const ImportedRideRequest = z
    .preprocess(derivePointType, z.discriminatedUnion("type", [ImportedRideRequestByCoordinates, ImportedRideRequestByStop], DISCRIMINATOR_ERROR))
    .openapi("ImportedRideRequest", {
    description: "Una fila del lote de importacion, discriminada por tipo (coordinates o stop). Cada fila se valida y persiste de forma independiente: una fila invalida no voltea el lote.",
});
export const ImportRideRequestsRequest = z
    .object({
    rideRequests: z
        .array(ImportedRideRequest)
        .min(1)
        .max(MAX_REQUESTS_PER_BATCH),
})
    .openapi("ImportRideRequestsRequest", {
    description: "Lote de importacion de solicitudes reales (hasta 500 filas). La API valida cada fila por separado y reporta el resultado fila por fila: el lote nunca se cae entero por una fila mala.",
});
export const RowResult = z.enum(["created", "duplicate", "invalid"]).openapi("RowResult", {
    description: "created: insertada en esta corrida; duplicate: ya existia una solicitud con ese id en la planta (idempotencia); invalid: la fila no paso la validacion y NO se inserto.",
});
export const ImportRowResult = z
    .object({
    index: Integer.min(0).openapi({
        description: "Posicion de la fila en el lote enviado (base 0).",
    }),
    requestId: RideRequestId.optional().openapi({
        description: "Id de la fila, cuando la fila lo trae legible (puede faltar en filas invalid con shape roto).",
    }),
    result: RowResult,
    reason: z.string().optional().openapi({
        description: "Motivo del rechazo, presente solo cuando result=invalid.",
    }),
})
    .openapi("ImportRowResult", {
    description: "Resultado de una fila del lote de importacion.",
});
export const ImportSummary = z
    .object({
    received: Integer.min(0),
    created: Integer.min(0),
    duplicates: Integer.min(0),
    invalid: Integer.min(0),
})
    .openapi("ImportSummary", { description: "Conteos del lote importado." });
export const ImportRideRequestsResponse = z
    .object({
    summary: ImportSummary,
    rows: z.array(ImportRowResult),
})
    .openapi("ImportRideRequestsResponse", {
    description: "Resumen del lote y resultado fila por fila, en el orden del lote enviado.",
});
export const RideRequestsQuery = z
    .object({
    date: OperatingDate.optional().openapi({
        description: "Filtra por fecha operativa exacta.",
    }),
    status: RideRequestStatus.optional().openapi({ description: "Filtra por estado." }),
    dateFrom: OperatingDate.optional().openapi({
        description: "Desde esta fecha operativa, inclusive (PB-17). Con dateTo, dateFrom no puede ser posterior.",
    }),
    dateTo: OperatingDate.optional().openapi({
        description: "Hasta esta fecha operativa, inclusive (PB-17).",
    }),
    service: VehicleType.optional().openapi({
        description: "Filtra por el servicio con que se despacha (el mismo de serviceType en la respuesta) (PB-17).",
    }),
    issue: DispatchIssue.optional().openapi({
        description: "Filtra por motivo de despacho (PB-17).",
    }),
})
    .openapi("RideRequestsQuery", {
    description: "Filtros del listado de solicitudes; se combinan entre si (y).",
});
export const ListRideRequestsResponse = z
    .object({ rideRequests: z.array(RideRequestResponse) })
    .openapi("ListRideRequestsResponse", {
    description: "Listado de solicitudes de la planta, filtrable por fecha operativa (exacta o rango), estado, servicio y motivo de despacho.",
});
// --- "Hoy no viajo" (R1.5 Fase A, pieza 3) -----------------------------------
// Aviso anticipado del pasajero de que no toma el transporte (dolor 2 de
// Unipar: la gente no se toma la shuttle y el recorrido fijo no se entera). En
// Fase A lo dispara el operador por el pasajero; en R3 la app KMP consume el
// mismo contrato. El reason es un catalogo cerrado: la metrica del cierre solo
// agrega si el reason es comparable; el comment libre es opcional y corto.
export const RideRequestParams = z
    .object({ siteId: SiteId, requestId: RideRequestId })
    .openapi("RideRequestParams", { description: "Planta y solicitud del path." });
export const OptOutReason = z
    .enum(["illness", "shift_change", "day_off", "other_transport", "other"])
    .openapi("OptOutReason", {
    description: "Catalogo cerrado del motivo por el que el pasajero avisa que no viaja: illness (enfermedad), shift_change (cambio de turno), day_off (franco), other_transport (va por sus medios) u other. Cerrado a proposito: el cierre diario agrega por motivo.",
});
export const CancelRideRequestRequest = z
    .object({
    reason: OptOutReason,
    comment: z.string().trim().min(1).max(200).optional().openapi({
        description: "Comentario libre del pasajero u operador (hasta 200 caracteres). Opcional: se omite cuando no hay nada que agregar. Queda en el historial append-only del viaje.",
        example: "Aviso el franco compensatorio del lunes",
    }),
})
    .openapi("CancelRideRequestRequest", {
    description: "Aviso 'hoy no viajo' sobre una solicitud. Antes del armado la solicitud pasa a cancelada; despues, el pasajero queda como opted_out en el viaje y su parada se libera. Idempotente: repetir el aviso devuelve el mismo estado.",
});
export const RideRequestCancellationEffect = z
    .enum(["cancelled_before_routing", "stop_released"])
    .openapi("RideRequestCancellationEffect", {
    description: "Efecto del aviso: cancelled_before_routing (la solicitud aun no tenia viaje; el armado no la considera) o stop_released (ya tenia viaje: el pasajero queda opted_out, su lugar en la parada se libera y, si era el ultimo pasajero de esa parada, la parada queda omitible sin borrarse del recorrido).",
});
export const CancelRideRequestResponse = z
    .object({
    rideRequest: RideRequestResponse,
    effect: RideRequestCancellationEffect,
})
    .openapi("CancelRideRequestResponse", {
    description: "Resultado del aviso 'hoy no viajo': la solicitud ya cancelada (conserva tripId si ya estaba armada, para la trazabilidad) y el efecto sobre el viaje.",
});
// --- Despacho de una solicitud desde la torre (PB-17) -------------------------
// La torre actua sobre UNA solicitud pendiente. retry: la re-evalua con el
// armado de siempre (mismo reparto, tope y cola). manual: crea el viaje de un
// pasajero para esa solicitud sola y lo ofrece a la cola de su servicio; la
// planta no elige proveedor (DD-9). "Resuelto por fuera" no es un modo: es
// cancelar con un motivo del catalogo (cancelRideRequest).
export const DispatchMode = z.enum(["retry", "manual"]).openapi("DispatchMode", {
    description: "retry: re-evalua la solicitud con el armado (si vuelve a fallar, queda pending con su motivo actualizado). manual: viaje de un pasajero para esa solicitud, sin tope ni consolidacion, ofrecido a la cola de su servicio; la planta no elige proveedor.",
});
export const DispatchRideRequestRequest = z
    .object({ mode: DispatchMode })
    .strict()
    .openapi("DispatchRideRequestRequest", {
    description: "Accion de la torre sobre una solicitud pendiente.",
});
export const DispatchRideRequestResponse = z
    .object({
    mode: DispatchMode,
    rideRequest: RideRequestResponse,
})
    .openapi("DispatchRideRequestResponse", {
    description: "La solicitud despues de la accion: assigned con tripId si se convirtio en viaje, o pending con dispatchIssue si el armado la volvio a rechazar (solo retry).",
});
//# sourceMappingURL=ride-requests.js.map